import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { TYPE_EVENT_OTHER, AI_EVENTS_STATUS } from '@/shared/config';
import { isValidHttpUrl } from '@/shared/lib';
import { useMergeState } from '@/shared/lib/customHooks';
import { toastrError } from '@/shared/lib/toastNotification';
import type { EditNormalInfoProps } from './editNormalInfo';

/**
 * Backs the basic pane of the AI strip edit dialog where comments, title and report flags are set. Mirrors the incoming strip or transfer payload into local state, tracks whether anything differs from the original, builds the image gallery from the captured strips, validates length and emptiness on done, and stops key events escaping the comment and title inputs.
 *
 * @param props - Pane props supplying the strip data, original data, transfer payload, the basic-info and removable flags and the update, close and enter-more-info callbacks.
 * @returns An object carrying isEditBasicInfo, couldRemoveStrip, imageRef, state, setState, listImages and the done, change, checkbox, more-info and reset handlers.
 */
const useEditNormalInfo = ({
  isEditBasicInfo = false,
  couldRemoveStrip = true,
  data,
  originalData,
  transferData,
  handleClickCloseButton,
  handleEnterEditMoreInfo,
  updateEditData,
}: Pick<
  EditNormalInfoProps,
  | 'isEditBasicInfo'
  | 'couldRemoveStrip'
  | 'data'
  | 'originalData'
  | 'transferData'
  | 'handleClickCloseButton'
  | 'handleEnterEditMoreInfo'
  | 'updateEditData'
>) => {
  const imageRef = useRef<any>();
  const [state, setState] = useMergeState<any>({
    isLoading: true,
    isLoadedImageError: false,
    isIncludedToReport: false,
    selected: false,
    technicianComments: '',
    title: '',
    stripImgs: [],
    draw: [],
    isRemoved: false,
    hasChanged: false,
    isOpenResetChangeModal: false,
    isOpenRemoveStripModal: false,
  });

  const [listImages, setListImages] = useState<any[]>([]);
  /**
   * Validates the strip annotation and pushes it up for saving. Rejects a comment over 520 characters or a title over 100 with a toast, blocks an empty comment or a missing title on other-type events with inline errors, closes without saving when nothing differs from the loaded data, and otherwise sends the assembled payload to updateEditData.
   *
   * @returns Nothing.
   */
  const onClickDone = () => {
    if (state.technicianComments && state.technicianComments.length > 520) {
      toastrError('Exceeded maximum of 520 characters in Technician comment', 'Error');
      return;
    }
    if (state.title && state.title.length > 100) {
      toastrError('Exceeded maximum of 100 characters in Title', 'Error');
      return;
    }
    if (!state.technicianComments.trim()) {
      setState({ commentError: 'Please enter a comment' });
      return;
    }
    if (
      !state.title.trim() &&
      (originalData?.type === TYPE_EVENT_OTHER ||
        (isEditBasicInfo && data?.type === TYPE_EVENT_OTHER))
    ) {
      setState({ titleError: 'Please enter a title' });
      return;
    }
    const { comments, isIncludedToReport, selected, status, title, draw, stripImgs, type } = data;
    const isChangeSelected =
      selected !== state.selected && (state.isIncludedToReport || isIncludedToReport);
    if (
      state.isIncludedToReport === isIncludedToReport &&
      !isChangeSelected &&
      state.technicianComments === (comments || '') &&
      state.isRemoved === (status === AI_EVENTS_STATUS.REMOVED) &&
      (type === TYPE_EVENT_OTHER ? title || '' : '') === (state.title || '') &&
      _.isEqual(state.draw, draw) &&
      _.isEqual(state.stripImgs, stripImgs)
    ) {
      handleClickCloseButton();
      return;
    }
    const updatedData = {
      id: data.id,
      isIncludedToReport: state.isIncludedToReport,
      selected: state.selected,
      isRemoved: state.isRemoved,
      comments: state.technicianComments,
      title: state.title,
      draw: state.draw,
      stripImgs: state.stripImgs,
      stripId: data.stripId,
      originalEventId: data.originalEventId,
      event: data?.event,
    };
    updateEditData(updatedData, true, data?.type);
  };

  /**
   * Records a field edit in the basic strip form and clears its validation error. Merges the named key into state, additionally blanking the title error for title edits and the comment error for technician comment edits.
   *
   * @param name - State key being written.
   * @param value - New value for that key.
   * @returns Nothing.
   */
  const onChange = (name: any, value: any) => {
    setState({ [name]: value });
    if (name === 'title') {
      setState({ titleError: '' });
    }
    if (name === 'technicianComments') {
      setState({ commentError: '' });
    }
  };

  /**
   * Flips a boolean option on the basic strip form. Inverts the current value stored under the given key.
   *
   * @param name - State key of the boolean being toggled.
   * @returns Nothing.
   */
  const onClickCheckbox = (name: any) => {
    setState({ [name]: !state[name] });
  };

  /**
   * Turns the captured strip markup into gallery entries for the image viewer. Drops the combined all.svg entry, wraps raw SVG text in an object URL while leaving real http URLs alone, and labels each entry with its position in the set.
   *
   * @param stripImgs - Captured strip images, either SVG markup or URLs.
   * @returns Gallery items carrying original, thumbnail and description fields.
   */
  const createItems = (stripImgs: any[]) => {
    const notIncludedAllStripImgs = _.filter(stripImgs, (x) => !_.includes(x, 'all.svg'));
    return _.map(notIncludedAllStripImgs, (stripImg, index) => {
      const svgBlob = new Blob([stripImg], { type: 'image/svg+xml' });
      const svgUrl = URL.createObjectURL(svgBlob);
      const formatImg = isValidHttpUrl(stripImg) ? stripImg : svgUrl;
      const description = `${index + 1}/${notIncludedAllStripImgs.length}`;
      return {
        original: formatImg,
        originalAlt: `ECG strip ${description}`,
        thumbnail: formatImg,
        thumbnailAlt: `ECG strip ${description} thumbnail`,
        description,
      };
    });
  };

  /**
   * Moves the current form values into the extended more-info editor. Bundles the strip images, drawings, technician comments, title, removal, include-in-report and selected flags and passes them to handleEnterEditMoreInfo.
   *
   * @returns Nothing.
   */
  const onClickEditMoreInfo = () => {
    const transferData = {
      stripImgs: state.stripImgs,
      draw: state.draw,
      technicianComments: state.technicianComments,
      title: state.title,
      isRemoved: state.isRemoved,
      isIncludedToReport: state.isIncludedToReport,
      selected: state.selected,
    };
    handleEnterEditMoreInfo(transferData);
  };

  /**
   * Restores the strip form to the values it was opened with. Rebuilds state from the original data, treating a REMOVED status as the removed flag and keeping the title only for other-type events, skips the loading spinner when the images are unchanged, and pushes the restored payload up through updateEditData without closing the modal.
   *
   * @returns Nothing.
   */
  const onClickResetChanges = () => {
    if (originalData) {
      const {
        stripImgs,
        comments,
        isIncludedToReport,
        selected,
        status,
        title,
        type,
        draw,
        isRemoved,
      } = originalData;
      const setStateObject = {
        stripImgs,
        draw,
        technicianComments: comments || '',
        title: type === TYPE_EVENT_OTHER ? title : '',
        isRemoved: isRemoved || status === AI_EVENTS_STATUS.REMOVED,
        isIncludedToReport,
        selected,
        isOpenResetChangeModal: false,
        commentError: '',
        titleError: '',
      };
      //* Cached image dont trigger onLoad
      if (_.isEqual(stripImgs, state.stripImgs)) {
        _.assign(setStateObject, { isLoading: false });
      }
      setState(setStateObject);
      const updatedData = {
        id: originalData.id,
        isIncludedToReport,
        selected,
        isRemoved: isRemoved || status === AI_EVENTS_STATUS.REMOVED,
        comments,
        title,
        draw,
        stripImgs,
        stripId: data.stripId,
        originalEventId: data.originalEventId,
      };
      updateEditData(updatedData, false);
    }
  };

  useEffect(() => {
    console.log('[editNormalInfo]-data', data);
    if (data) {
      const {
        stripImgs,
        draw,
        comments,
        isIncludedToReport,
        selected,
        status,
        title,
        type,
        isRemoved,
      } = data;
      const setStateObject = {
        stripImgs,
        draw,
        technicianComments: comments || '',
        title: type === TYPE_EVENT_OTHER ? title : '',
        isRemoved: isRemoved || status === AI_EVENTS_STATUS.REMOVED,
        isIncludedToReport,
        selected,
        isLoading: true,
      };
      setState(setStateObject);
    }
  }, [data]);

  useEffect(() => {
    console.log('[editNormalInfo]-transferData', transferData);
    if (transferData) {
      const {
        stripImgs,
        draw,
        technicianComments,
        title,
        isRemoved,
        isIncludedToReport,
        selected,
      } = transferData;
      const setStateObject = {
        stripImgs,
        draw,
        technicianComments,
        title,
        isRemoved,
        isIncludedToReport,
        selected,
        isLoading: true,
      };
      //* Cached image dont trigger onLoad
      if (_.isEqual(stripImgs, state.stripImgs)) {
        _.assign(setStateObject, { isLoading: false });
      }
      setState(setStateObject);
    }
  }, [transferData]);

  useEffect(() => {
    if (originalData) {
      const { stripImgs, draw, comments, isIncludedToReport, selected, title, type } = originalData;
      const isChangeSelected =
        selected !== state.selected && (state.isIncludedToReport || isIncludedToReport);
      if (state.isRemoved) {
        setState({ hasChanged: true });
      } else if (
        isIncludedToReport === state.isIncludedToReport &&
        !isChangeSelected &&
        comments === state.technicianComments &&
        (type === TYPE_EVENT_OTHER ? title || '' : '') === (state.title || '') &&
        _.isEqual(stripImgs, state.stripImgs) &&
        _.isEqual(draw, state.draw)
      ) {
        setState({ hasChanged: false });
      } else {
        setState({ hasChanged: true });
      }
    }
  }, [
    state.stripImgs,
    state.draw,
    state.technicianComments,
    state.title,
    state.isRemoved,
    state.isIncludedToReport,
    state.selected,
    originalData,
  ]);

  useEffect(() => {
    if (state.stripImgs?.length) {
      const stripImgs = createItems(state.stripImgs);
      setListImages(stripImgs);
    }
  }, [state.stripImgs]);

  useEffect(() => {
    if (!state.isIncludedToReport) {
      setState({ selected: false });
    }
  }, [state.isIncludedToReport]);

  useEffect(() => {
    const technicianInput = document.getElementById('technicianInput');
    if (technicianInput) {
      technicianInput.addEventListener('keydown', (event) => {
        event.stopPropagation();
      });
    }
    const titleInput = document.getElementById('titleInput');
    if (titleInput) {
      titleInput.addEventListener('keydown', (event) => {
        event.stopPropagation();
      });
    }
  }, [originalData?.type, data?.type]);

  return {
    isEditBasicInfo,
    couldRemoveStrip,
    imageRef,
    state,
    setState,
    listImages,
    onClickDone,
    onChange,
    onClickCheckbox,
    onClickEditMoreInfo,
    onClickResetChanges,
  };
};

export default useEditNormalInfo;
